import { useEffect, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { useLocation } from "react-router-dom"

import { SvgIcon } from "@components/common"
import endpoints from "@config/endpoints"
import { selectActiveTeeup } from "@selectors/activeTeeup"
import { selectUserInfo } from "@selectors/user"

import api from "../../api"

import "./index.scss"

// INBOX-REBUILD 2026-07-03 (Part 1 spec): Ale's floating "Coowe AI" pill + panel, ported as a
// STATIC SHELL ONLY. No API wiring, no message state beyond open/closed.
//
// PEOPLE-ALIGN 2026-07-03 (Part 3): promoted to this shared location, still a static shell.
//
// WINDOW-ARCH 2026-07-03: reworked into a TRUE SIDE WINDOW mounted ONCE at the app shell
// (App.js) as a grid SIBLING of the .main-window content window -- opening it animates the
// third grid column (0 <-> 360px <-> 560px, her --asst / toggleAsstWide widths, her .28s
// cubic-bezier curve) so the content window narrows beside it. Collapsed state = the floating
// pill. Header order matches hers: logo + title + spacer + expand + new-thread + close.
//
// WINDOW-ARCH 2026-07-03 (QJ corrections): the panel body layout was originally an EMPTY
// conversation container by design (QJ: "blank chat container, nothing working"). The logo (pill
// AND header) is the ACTUAL CooWe mark (`coowe-small-icon-blue.svg`, the exact asset the sidebar
// uses), not a placeholder star. All panel accents use the sidebar's #7FB1E3 brand blue (the
// previous dark #246FE2 plus-icon and the black send glyph are replaced with brand-blue/white
// inline marks).
//
// AI-LIFECYCLE-WEB 2026-07-09: the body is no longer a static empty shell -- it is wired to the
// real backend POST /v1/ai/coordination-chat (api/routers/ai_create_draft.py::coordination_chat),
// a genuinely separate model call from the create-flow rail (CooweAiRail.js -> /ai/create-draft).
// Same never-500/degrade-soft contract: on failure the panel shows an explicit offline status +
// error line, never a fabricated client-side reply (no stub parser exists for this surface).
//
// Terminology rule: "Conversation", never "chat", in every user-facing string here.
//
// AI-CONTEXT 2026-07-03 (QJ-approved): two behavior additions, still inside this component only.
// 1) Default-open on a TeeUp detail route: the panel opens itself when the user lands on
//    /teeUps/:id, unless the user explicitly closed it earlier in this browser session (tracked
//    via a sessionStorage flag so it doesn't force itself back open on later teeup navigations).
// 2) A quiet context chip under the header: "Supporting: {TeeUp title}" on a teeup detail
//    (resolved from the activeTeeup redux slice, matched against the route id so a stale title
//    never flashes), "Your assistant" everywhere else / on any resolution failure.
//
// AI-PILL-CREATEAI 2026-07-03 (dev-fix): `/create-ai` (CreateAiPage) is itself an AI surface with
// its own conversation rail + Send button -- this global floating pill/panel is redundant there
// AND, being a fixed bottom-right overlay, sits on top of that rail's Send button and intercepts
// pointer clicks (Enter-key submit still worked, but a real click did not -- flagged by the
// AI-SLICE1 track). Suppress this component entirely (no pill, no panel, no DOM node) whenever
// the route is /create-ai (or a sub-route of it); every other route/behavior above is unchanged.
const SESSION_CLOSED_IN_TEEUP_KEY = "coowe-ai-closed-in-teeup"
const TEEUP_DETAIL_ROUTE_RE = /^\/teeUps\/([^/]+)/
const CREATE_AI_ROUTE_RE = /^\/create-ai(\/|$)/
const AI_CHAT_TIMEOUT_MS = 130000

let msgSeq = 0
const nextMsgId = () => `cai_${Date.now()}_${msgSeq++}`

const CooweAI = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isWide, setIsWide] = useState(false)
  const [inputValue, setInputValue] = useState("")
  const [messages, setMessages] = useState<
    { id: string; role: "user" | "ai"; text: string; isError?: boolean }[]
  >([])
  const [isThinking, setIsThinking] = useState(false)
  const [aiStatus, setAiStatus] = useState<"unknown" | "online" | "offline">("unknown")
  const threadEndRef = useRef<HTMLDivElement>(null)

  const location = useLocation()
  const pathname = location?.pathname || ""
  const isCreateAiRoute = CREATE_AI_ROUTE_RE.test(pathname)
  const userInfo = useSelector(selectUserInfo)
  const organizerName = userInfo?.firstName || userInfo?.name || ""

  const routeMatch = TEEUP_DETAIL_ROUTE_RE.exec(pathname)
  const isTeeupDetailRoute = !!routeMatch
  const routeTeeupId = routeMatch?.[1]

  const activeTeeup = useSelector(state => {
    try {
      return selectActiveTeeup(state)
    } catch {
      return null
    }
  })

  const teeupTitle =
    isTeeupDetailRoute &&
    activeTeeup?.name &&
    routeTeeupId &&
    String(activeTeeup?.id) === String(routeTeeupId)
      ? activeTeeup.name
      : null

  const contextLabel = teeupTitle ? `Supporting: ${teeupTitle}` : "Your assistant"

  // AI-LIFECYCLE-WEB 2026-07-09: connection-status ping, same pattern as CooweAiRail.js -- cheap
  // GET, no LLM call, safe on every mount so the status dot never lies about reachability.
  useEffect(() => {
    let cancelled = false
    api.client
      .get(endpoints.ai.coordinationChatHealth())
      .then(({ data }: any) => {
        if (!cancelled) setAiStatus(data && data.configured && data.reachable ? "online" : "offline")
      })
      .catch(() => {
        if (!cancelled) setAiStatus("offline")
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, isThinking])

  useEffect(() => {
    if (!isTeeupDetailRoute) return

    let closedInSession = false
    try {
      closedInSession = sessionStorage.getItem(SESSION_CLOSED_IN_TEEUP_KEY) === "1"
    } catch {
      closedInSession = false
    }

    if (!closedInSession) {
      setIsOpen(true)
    }
  }, [pathname, isTeeupDetailRoute])

  const handleClose = () => {
    setIsOpen(false)
    if (isTeeupDetailRoute) {
      try {
        sessionStorage.setItem(SESSION_CLOSED_IN_TEEUP_KEY, "1")
      } catch {
        // sessionStorage unavailable (e.g. privacy mode) -- degrade silently, panel still closes.
      }
    }
  }

  // AI-LIFECYCLE-WEB 2026-07-09: real backend turn -- POST /v1/ai/coordination-chat. New-thread
  // (the header "+") resets messages so a fresh conversation isn't dragged down by stale context.
  const handleNewThread = () => {
    setMessages([])
  }

  const handleSend = async () => {
    const trimmed = inputValue.trim()
    if (!trimmed || isThinking) return

    const userMsg = { id: nextMsgId(), role: "user" as const, text: trimmed }
    const priorMessages = [...messages, userMsg]
    setMessages(prev => [...prev, userMsg])
    setInputValue("")
    setIsThinking(true)

    // AI-LIFECYCLE-WEB #270 (2026-07-09): per-screen contextual grounding. On a TeeUp detail
    // route, prepend a NON-VISIBLE context turn (never added to the rendered `messages`, mirroring
    // the create-draft rail's CONTEXT line) carrying the live TeeUp state from redux -- the real
    // lifecycle status of THIS TeeUp -- so the assistant can answer "what's left / what should I
    // do next" grounded in fact instead of generically. No UI change (the panel body stays the
    // blank thread per the WINDOW-ARCH ruling); no backend schema change (the endpoint forwards
    // messages to the LLM verbatim, and its system prompt already forbids fabricating beyond what
    // it is given). Only real values from the activeTeeup slice are sent -- never a guess.
    const apiMessages = priorMessages.map(m => ({
      role: m.role === "ai" ? "assistant" : ("user" as "user" | "assistant"),
      content: m.text,
    }))
    if (teeupTitle && activeTeeup?.status) {
      apiMessages.unshift({
        role: "user",
        content:
          "CONTEXT (not part of the conversation, do not reply to this line): the user is viewing " +
          `the TeeUp "${teeupTitle}", whose current lifecycle status is "${activeTeeup.status}". ` +
          "Ground any advice in this real status; do not invent details you were not given.",
      })
    }

    try {
      const { data } = await api.client.post(
        endpoints.ai.coordinationChat(),
        {
          messages: apiMessages,
          organizer_name: organizerName,
          teeup_id: teeupTitle ? String(routeTeeupId || "") : "",
          teeup_title: teeupTitle || "",
          timezone:
            (typeof Intl !== "undefined" && Intl.DateTimeFormat().resolvedOptions().timeZone) ||
            "",
        },
        { timeout: AI_CHAT_TIMEOUT_MS }
      )
      const reply = (data && data.reply) || ""
      if (!reply) throw new Error("empty AI response")
      setAiStatus("online")
      setMessages(prev => [...prev, { id: nextMsgId(), role: "ai", text: reply }])
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("coordination-chat call failed:", (e as any)?.message || e)
      setAiStatus("offline")
      setMessages(prev => [
        ...prev,
        {
          id: nextMsgId(),
          role: "ai",
          text: "The assistant is offline right now -- try again in a moment.",
          isError: true,
        },
      ])
    } finally {
      setIsThinking(false)
    }
  }

  // AI-PILL-CREATEAI: after all hooks above have run (rules-of-hooks safe), render nothing on
  // /create-ai -- no pill, no panel, no DOM node at all, so it can never intercept a pointer
  // event on that page's own conversation-rail Send button.
  if (isCreateAiRoute) {
    return null
  }

  return (
    <div
      data-testid="coowe-ai-window"
      className={
        "coowe-ai-window" +
        (isOpen ? " coowe-ai-window--open" : "") +
        (isWide ? " coowe-ai-window--wide" : "")
      }>
      {!isOpen && (
        // Ale's #fab shape: fixed bottom-right pill, --ink background, the real CooWe mark +
        // "Coowe AI" label inline.
        <button
          type="button"
          data-testid="coowe-ai-pill"
          aria-label="Open Coowe AI"
          onClick={() => setIsOpen(true)}
          className="fixed bottom-[18px] right-5 z-40 flex items-center gap-[9px] rounded-full bg-[#161A22] text-white px-[18px] py-[11px] text-[13px] font-medium shadow-[0_8px_24px_-8px_#00000080] hover:bg-[#20242E] transition-colors">
          <span className="flex size-5 items-center justify-center rounded-full bg-white">
            <SvgIcon name="coowe-small-icon-blue" className="!size-3" />
          </span>
          Coowe AI
        </button>
      )}

      {isOpen && (
        <div data-testid="coowe-ai-panel" className="coowe-ai-panel">
          {/* .asst-head: logo + title + spacer + expand + new-thread + close */}
          <div className="coowe-ai-panel__head">
            <span className="flex size-7 items-center justify-center rounded-[7px] bg-[#EAF2FC]">
              <SvgIcon name="coowe-small-icon-blue" className="!size-[15px]" />
            </span>
            <div className="flex min-w-0 flex-col justify-center">
              <div className="text-[15px] font-semibold text-[#16181D]">Coowe AI</div>
              <div
                data-testid="coowe-ai-context-chip"
                title={contextLabel}
                className="max-w-[200px] truncate text-[11px] font-medium text-[#7FB1E3]">
                {contextLabel}
              </div>
            </div>
            <div className="flex-1" />
            <span
              className={`coowe-ai-panel__status coowe-ai-panel__status--${aiStatus}`}
              data-testid="coowe-ai-connection-indicator"
              title={
                aiStatus === "online"
                  ? "Assistant online"
                  : aiStatus === "offline"
                  ? "Assistant offline"
                  : "Checking assistant connection..."
              }>
              <span className="coowe-ai-panel__statusDot" />
            </span>
            <button
              type="button"
              data-testid="coowe-ai-expand"
              title={isWide ? "Shrink" : "Expand"}
              onClick={() => setIsWide(w => !w)}
              className="rounded p-1.5 text-[#5C616B] hover:bg-[#F0F0F2]">
              {/* diagonal expand arrows, stroke inherits currentColor */}
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round">
                <path d="M9.5 6.5L14 2M14 2h-4M14 2v4" />
                <path d="M6.5 9.5L2 14M2 14h4M2 14v-4" />
              </svg>
            </button>
            <button
              type="button"
              data-testid="coowe-ai-new-thread"
              title="New conversation"
              onClick={handleNewThread}
              className="rounded p-1.5 text-[#5C616B] hover:bg-[#F0F0F2]">
              {/* plain plus, currentColor -- replaces the old dark-blue (#246FE2) plus-icon asset */}
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round">
                <path d="M8 3.5v9M3.5 8h9" />
              </svg>
            </button>
            <button
              type="button"
              data-testid="coowe-ai-close"
              title="Close"
              onClick={handleClose}
              className="rounded p-1.5 text-[#5C616B] hover:bg-[#F0F0F2]">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round">
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            </button>
          </div>

          {/* .asst-body -- real conversation thread (AI-LIFECYCLE-WEB 2026-07-09). Empty state
              keeps the original blank-container look until the first turn. */}
          <div data-testid="coowe-ai-thread" className="coowe-ai-panel__body" aria-label="Conversation">
            {messages.map(m => (
              <div
                key={m.id}
                className={`coowe-ai-msg coowe-ai-msg--${m.role}${m.isError ? " coowe-ai-msg--error" : ""}`}>
                {m.text}
              </div>
            ))}
            {isThinking && (
              <div className="coowe-ai-msg coowe-ai-msg--ai coowe-ai-msg--thinking" data-testid="coowe-ai-thinking">
                <span className="coowe-ai-thinking-dot" />
                <span className="coowe-ai-thinking-dot" />
                <span className="coowe-ai-thinking-dot" />
              </div>
            )}
            <div ref={threadEndRef} />
          </div>

          {/* .asst-foot */}
          <div className="coowe-ai-panel__foot">
            <div className="flex items-center gap-2 rounded-full border border-[#E7E7EA] bg-[#F5F7FA] px-3 py-2">
              <input
                data-testid="coowe-ai-input"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === "Enter") handleSend()
                }}
                placeholder="or ask anything you need"
                className="flex-1 bg-transparent text-[13px] outline-none placeholder:text-[#9A9CA3]"
              />
              <button
                type="button"
                data-testid="coowe-ai-send"
                onClick={handleSend}
                title="Send"
                className="flex size-7 items-center justify-center rounded-full bg-[#7FB1E3] text-white">
                {/* paper-plane, white on brand blue */}
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <path d="M14 2L7 9M14 2l-4.5 12-2.5-5-5-2.5L14 2z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CooweAI
